import {
  ArrowLeft,
  Check,
  Eye,
  Plus,
  Redo2,
  Settings2,
  SlidersHorizontal,
  Undo2,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router'
import { VersionesEnIdiomas } from '../motor/VersionesEnIdiomas'

import { Aviso, Boton, BotonDeIcono, Modal, Pastilla, Pestanas, clases } from '../componentes/ui'
import { Editor, type Edicion } from '../motor/Editor'
import { esDe } from '../motor/errores'
import { campoPorNombre } from '../motor/esquema'
import { useFormulario, useValor } from '../motor/Formulario'
import { RespuestasDeUnFormulario } from '../paginas/Envios'
import { seccionDelError, seccionesDeAjustes } from './ajustes'
import { AjustesDelCampo } from './AjustesDelCampo'
import { AjustesDelFormulario } from './AjustesDelFormulario'
import {
  claveDeFila,
  cortar,
  deshacer,
  desplazar,
  duplicar,
  type Fila,
  historialVacio,
  insertar,
  mover,
  nuevoCampo,
  quitar,
  registrar,
  rehacer,
} from './estado'
import { Lienzo } from './Lienzo'
import { useMarcaDeLaWeb } from './marca'
import { Paleta } from './Paleta'
import { useResumenDeFormularios } from './resumen'
import { VistaPrevia } from './VistaPrevia'

/**
 * El constructor de formularios (CLAUDE.md §9): **el formulario tal como se verá** en el
 * centro, la paleta de campos y los ajustes del campo elegido a la derecha, y arriba el
 * nombre, las pestañas Editor · Ajustes · Respuestas, «Vista previa» y «Guardar».
 *
 * No es otro editor: es **otra forma de ordenar el editor del motor** (`Editor` con
 * `disposicion`). El documento, el `PATCH` con su cabecera de concurrencia y su 409, la
 * guarda de cambios sin guardar y los errores del núcleo son los de siempre, y los ajustes
 * de cada campo y de la pestaña Ajustes son los controles del motor sobre el esquema que
 * sirve el núcleo. Lo propio es el lienzo, la paleta y la vista previa.
 *
 * La pestaña va en el `#` de la dirección (`#ajustes`, `#respuestas`; `#vista-previa` abre
 * la vista previa): cambiarla no cambia de página, así que no pregunta por los cambios sin
 * guardar, y un enlace del listado lleva directo a ella.
 */
type Pestana = 'editor' | 'ajustes' | 'respuestas'
type Valor = Record<string, unknown>

export const ConstructorDeFormulario = ({
  espacio,
  sitio,
  base,
}: {
  base: string
  espacio: number
  sitio: number
}) => {
  const { id } = useParams()
  // «Nuevo formulario» se elige en el listado (plantilla o en blanco): la ficha nueva es eso.
  if (!id || !/^\d+$/.test(id))
    return <Navigate replace to={id === 'nuevo' ? `${base}?nuevo=1` : base} />
  return (
    <Editor
      base={base}
      coleccion="formularios"
      disposicion={(edicion) => (
        <Constructor base={base} edicion={edicion} espacio={espacio} sitio={sitio} />
      )}
      espacio={espacio}
      id={Number(id)}
      sitio={sitio}
    />
  )
}

/** Si la pantalla es estrecha (la barra de la derecha pasa a un cajón). */
const ANCHO_DE_LA_BARRA = '(min-width: 1024px)'
const useAncha = () =>
  useSyncExternalStore(
    (avisar) => {
      const consulta = window.matchMedia(ANCHO_DE_LA_BARRA)
      consulta.addEventListener('change', avisar)
      return () => consulta.removeEventListener('change', avisar)
    },
    () => window.matchMedia(ANCHO_DE_LA_BARRA).matches,
  )

const Constructor = ({
  edicion,
  espacio,
  sitio,
  base,
}: {
  base: string
  edicion: Edicion
  espacio: number
  sitio: number
}) => {
  const { valores, cambiar, reemplazar, escucharCambios, errores, soloLectura } = useFormulario()
  const navegar = useNavigate()
  const { hash } = useLocation()
  const ancha = useAncha()
  const { esquema } = edicion
  const marca = useMarcaDeLaWeb(sitio)
  const { data: resumen } = useResumenDeFormularios(sitio)
  const bloques = campoPorNombre(esquema.campos, 'campos')?.bloques ?? []
  const campos = useMemo(
    () => (Array.isArray(valores.campos) ? (valores.campos as Fila[]) : []),
    [valores.campos],
  )
  const secciones = useMemo(() => seccionesDeAjustes(esquema.campos), [esquema])

  const deLaDireccion = hash.replace(/^#/, '')
  const pestana: Pestana =
    deLaDireccion === 'ajustes' || deLaDireccion === 'respuestas' ? deLaDireccion : 'editor'
  const [previa, setPrevia] = useState(deLaDireccion === 'vista-previa')
  const [seccion, setSeccion] = useState(secciones[0]?.clave ?? 'general')
  const [seleccion, setSeleccion] = useState<null | string>(null)
  const [lateral, setLateral] = useState<'anadir' | 'ajustes'>('anadir')
  const [cajon, setCajon] = useState(false)
  const [pidiendo, setPidiendo] = useState<null | number>(null)
  const [enfocar, setEnfocar] = useState<null | { clave: string; vez: number }>(null)
  const [quitado, setQuitado] = useState<null | string>(null)

  const irA = (p: Pestana) => navegar({ hash: p === 'editor' ? '' : p }, { replace: true })

  // --- Deshacer y rehacer -------------------------------------------------------------
  const historial = useRef(historialVacio<Valor>())
  useEffect(
    () =>
      escucharCambios(
        ({ ruta, antes }) =>
          (historial.current = registrar(historial.current, ruta, antes, Date.now())),
      ),
    [escucharCambios],
  )
  const puedeDeshacer = historial.current.pasado.length > 0
  const puedeRehacer = historial.current.futuro.length > 0
  const deshacerUno = () => {
    const r = deshacer(historial.current, valores)
    if (!r) return
    historial.current = r.historial
    reemplazar(r.valores)
    setQuitado(null)
  }
  const rehacerUno = () => {
    const r = rehacer(historial.current, valores)
    if (!r) return
    historial.current = r.historial
    reemplazar(r.valores)
  }

  // --- El campo elegido -----------------------------------------------------------------
  const indiceElegido =
    seleccion === null ? -1 : campos.findIndex((f, i) => claveDeFila(f, i) === seleccion)
  const ultimoIndice = useRef<null | number>(null)
  useEffect(() => {
    if (indiceElegido >= 0) {
      ultimoIndice.current = indiceElegido
      return
    }
    if (seleccion === null) return
    // Al guardar, los campos nuevos reciben su `id` del núcleo y cambian de clave: se sigue en el mismo sitio.
    const i = ultimoIndice.current
    setSeleccion(i !== null && campos[i] ? claveDeFila(campos[i]!, i) : null)
  }, [indiceElegido, seleccion, campos])

  const elegir = (i: number) => {
    const fila = campos[i]
    if (!fila) return
    setSeleccion(claveDeFila(fila, i))
    setLateral('ajustes')
    if (!ancha) setCajon(true)
  }

  // --- Lo que cambia la lista de campos: cada cosa, un paso del historial -----------------
  const ponerCampos = (siguientes: Fila[]) => {
    historial.current = cortar(historial.current)
    cambiar('campos', siguientes)
  }
  const anadir = (tipo: string, posicion: number, abrirAjustes: boolean) => {
    const bloque = bloques.find((b) => b.slug === tipo)
    if (!bloque || soloLectura) return
    const nuevo = nuevoCampo(bloque, campos)
    ponerCampos(insertar(campos, nuevo, posicion))
    const clave = String(nuevo._clave)
    setSeleccion(clave)
    setEnfocar({ clave, vez: Date.now() })
    if (abrirAjustes) setLateral('ajustes')
    if (!ancha) setCajon(abrirAjustes)
  }
  const moverCampo = (desde: number, hueco: number) => {
    const r = mover(campos, desde, hueco)
    if (r.campos === campos) return
    ponerCampos(r.campos)
    setSeleccion(claveDeFila(r.campos[r.indice]!, r.indice))
  }
  const desplazarCampo = (indice: number, paso: -1 | 1) => {
    const r = desplazar(campos, indice, paso)
    if (r.campos === campos) return
    ponerCampos(r.campos)
    setEnfocar({ clave: claveDeFila(r.campos[r.indice]!, r.indice), vez: Date.now() })
  }
  const duplicarCampo = (indice: number) => {
    const r = duplicar(campos, indice)
    if (r.campos === campos) return
    ponerCampos(r.campos)
    const clave = claveDeFila(r.campos[r.indice]!, r.indice)
    setSeleccion(clave)
    setEnfocar({ clave, vez: Date.now() })
  }
  const quitarCampo = (indice: number) => {
    const fila = campos[indice]
    if (!fila) return
    ponerCampos(quitar(campos, indice))
    if (indice === indiceElegido) setSeleccion(null)
    setQuitado(String(fila.etiqueta || fila.clave || 'El campo'))
  }

  // «Quitado · Deshacer» se va solo.
  useEffect(() => {
    if (!quitado) return
    const t = window.setTimeout(() => setQuitado(null), 12_000)
    return () => window.clearTimeout(t)
  }, [quitado])

  // --- Al guardar con errores: al primero, en su pestaña y en su campo o su sección --------
  useEffect(() => {
    const rutas = Object.keys(errores)
    if (rutas.length === 0) return
    const enUnCampo = rutas.map((r) => /^campos\.(\d+)(?:\.|$)/.exec(r)).find(Boolean)
    if (enUnCampo) {
      const i = Number(enUnCampo[1])
      if (pestana !== 'editor') irA('editor')
      if (campos[i]) {
        setSeleccion(claveDeFila(campos[i]!, i))
        setLateral('ajustes')
        if (!ancha) setCajon(true)
        // El campo marcado, a la vista: puede estar muy abajo en un formulario largo.
        window.requestAnimationFrame(() => document.querySelector(`[data-campo="${i}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }))
      }
      return
    }
    const deAjustes = rutas.map((r) => seccionDelError(r, secciones)).find(Boolean)
    if (deAjustes) {
      if (pestana !== 'ajustes') irA('ajustes')
      setSeccion(deAjustes)
    }
    // Solo cuando llegan errores nuevos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errores])

  // --- Atajos: Ctrl+S guarda; Ctrl+Z / Ctrl+Mayús+Z deshacen y rehacen fuera de un campo de texto ---
  const atajos = useRef({ guardar: edicion.guardar, deshacerUno, rehacerUno })
  atajos.current = { guardar: edicion.guardar, deshacerUno, rehacerUno }
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return
      const tecla = e.key.toLowerCase()
      if (tecla === 's') {
        e.preventDefault()
        void atajos.current.guardar()
        return
      }
      // Dentro de un campo de texto, deshacer es el del propio campo.
      if (
        e.target instanceof Element &&
        e.target.closest('input, textarea, select, [contenteditable="true"]')
      )
        return
      if (tecla === 'z' && !e.shiftKey) {
        e.preventDefault()
        atajos.current.deshacerUno()
      } else if ((tecla === 'z' && e.shiftKey) || tecla === 'y') {
        e.preventDefault()
        atajos.current.rehacerUno()
      }
    }
    document.addEventListener('keydown', alTeclear)
    return () => document.removeEventListener('keydown', alTeclear)
  }, [])

  // El aviso de «Guardado.» se va solo; los de error se quedan hasta que se lean.
  const { mensaje, cerrarMensaje } = edicion
  useEffect(() => {
    if (mensaje?.tono !== 'ok') return
    const t = window.setTimeout(cerrarMensaje, 4000)
    return () => window.clearTimeout(t)
  }, [mensaje, cerrarMensaje])

  const datos = edicion.id !== null ? resumen?.get(edicion.id) : undefined
  const estado = typeof valores.estado === 'string' ? valores.estado : ''
  const etiquetaDeEstado = campoPorNombre(esquema.campos, 'estado')?.opciones?.find(
    (o) => o.valor === estado,
  )?.etiqueta
  const ajustesConErrores = Object.keys(errores).some((r) => seccionDelError(r, secciones) !== null)
  const camposConErrores = Object.keys(errores).some((r) => esDe(r, 'campos'))
  const fila = indiceElegido >= 0 ? campos[indiceElegido] : undefined
  const bloqueElegido = fila ? bloques.find((b) => b.slug === fila.blockType) : undefined

  const barraLateral = (
    <div className="flex h-full min-h-0 flex-col">
      <div
        aria-label="Barra del constructor"
        className="grid shrink-0 grid-cols-2 gap-1 border-b border-linea p-1.5"
        role="tablist"
      >
        {(
          [
            ['anadir', 'Añadir campos', <Plus key="i" />],
            ['ajustes', 'Ajustes del campo', <SlidersHorizontal key="i" />],
          ] as const
        ).map(([clave, etiqueta, icono]) => (
          <button
            aria-selected={lateral === clave}
            className={clases(
              'inline-flex items-center justify-center gap-1.5 rounded-boton px-2 py-2 text-[13px] font-medium transition-colors [&>svg]:size-4',
              lateral === clave
                ? 'bg-violeta-suave text-violeta'
                : 'text-tinta-2 hover:bg-papel-3 hover:text-tinta',
            )}
            key={clave}
            onClick={() => setLateral(clave)}
            role="tab"
            type="button"
          >
            {icono}
            {etiqueta}
          </button>
        ))}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4" role="tabpanel">
        {lateral === 'anadir' ? (
          <Paleta
            alElegir={(tipo) =>
              anadir(tipo, indiceElegido >= 0 ? indiceElegido + 1 : campos.length, false)
            }
            ayuda={
              indiceElegido >= 0
                ? 'Pulsa un campo para añadirlo debajo del elegido, o arrástralo al formulario.'
                : 'Pulsa un campo para añadirlo al final, o arrástralo al formulario.'
            }
            bloques={soloLectura ? [] : bloques}
          />
        ) : fila ? (
          <AjustesDelCampo
            alDuplicar={() => duplicarCampo(indiceElegido)}
            alQuitar={() => quitarCampo(indiceElegido)}
            bloque={bloqueElegido}
            indice={indiceElegido}
            key={claveDeFila(fila, indiceElegido)}
            soloLectura={soloLectura}
            total={campos.length}
          />
        ) : (
          <div className="flex flex-col items-center gap-2 py-10 text-center">
            <span className="flex size-10 items-center justify-center rounded-full bg-papel-3 text-tenue">
              <Settings2 aria-hidden className="size-5" />
            </span>
            <p className="text-sm font-medium">Elige un campo</p>
            <p className="max-w-60 text-xs text-tenue">
              Pulsa un campo del formulario para cambiar su etiqueta, si es obligatorio, sus
              opciones o cuándo se enseña.
            </p>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div>
      {/* La cabecera: el nombre, el estado, deshacer, la vista previa y guardar; y las pestañas. Se queda arriba al bajar. */}
      <div className="sticky top-14 z-[6] -mx-4 -mt-7 mb-6 bg-fondo/95 px-4 pt-4 backdrop-blur md:-mx-8 md:px-8">
        <Link
          className="mb-1 inline-flex items-center gap-1 text-[13px] text-tenue hover:text-tinta"
          to={base}
        >
          <ArrowLeft className="size-3.5" /> Formularios
        </Link>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <NombreDelFormulario soloLectura={soloLectura} />
          {etiquetaDeEstado && (
            <Pastilla clave={estado === 'activo' ? 'publicado' : 'sin-publicar'}>
              {etiquetaDeEstado}
            </Pastilla>
          )}
          {edicion.sucio && <span className="text-xs text-tenue">Cambios sin guardar</span>}
          <div className="ml-auto flex items-center gap-1.5">
            {!soloLectura && (
              <>
                <BotonDeIcono
                  disabled={!puedeDeshacer}
                  etiqueta="Deshacer (Ctrl+Z)"
                  onClick={deshacerUno}
                >
                  <Undo2 />
                </BotonDeIcono>
                <BotonDeIcono
                  disabled={!puedeRehacer}
                  etiqueta="Rehacer (Ctrl+Mayús+Z)"
                  onClick={rehacerUno}
                >
                  <Redo2 />
                </BotonDeIcono>
                <span aria-hidden className="mx-1 h-5 w-px bg-linea-fuerte" />
              </>
            )}
            <Boton icono={<Eye />} onClick={() => setPrevia(true)}>
              Vista previa
            </Boton>
            {!soloLectura && (
              <Boton
                cargando={edicion.guardando}
                disabled={!edicion.sucio || edicion.guardando}
                icono={edicion.sucio ? undefined : <Check />}
                onClick={() => void edicion.guardar()}
                tipo="primario"
              >
                {edicion.sucio ? 'Guardar' : 'Guardado'}
              </Boton>
            )}
          </div>
        </div>
        {/* Sus versiones en los otros idiomas de la web (§37): cada una es su propio formulario. */}
        <div className="mt-2">
          <VersionesEnIdiomas base={base} coleccion="formularios" id={edicion.id} sitio={sitio} sucio={edicion.sucio} />
        </div>
        <div className="mt-2">
          <Pestanas
            activa={pestana}
            alCambiar={(c) => irA(c as Pestana)}
            pestanas={[
              { clave: 'editor', etiqueta: 'Editor', aviso: camposConErrores },
              { clave: 'ajustes', etiqueta: 'Ajustes', aviso: ajustesConErrores },
              ...(edicion.id !== null
                ? [
                    {
                      clave: 'respuestas',
                      etiqueta: (
                        <>
                          Respuestas
                          {typeof datos?.respuestas === 'number' && (
                            <span
                              className={clases(
                                'rounded-pastilla px-1.5 text-xs tabular-nums',
                                datos.nuevas ? 'bg-violeta text-white' : 'bg-papel-3 text-tinta-2',
                              )}
                              title={datos.nuevas ? `${datos.nuevas} sin leer` : undefined}
                            >
                              {datos.respuestas.toLocaleString('es')}
                            </span>
                          )}
                        </>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </div>
      </div>

      {edicion.mensaje && (
        <div className="mb-5 flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <Aviso tono={edicion.mensaje.tono}>{edicion.mensaje.texto}</Aviso>
          </div>
          <BotonDeIcono etiqueta="Cerrar el aviso" onClick={edicion.cerrarMensaje} pequeno>
            <X />
          </BotonDeIcono>
        </div>
      )}

      {pestana === 'editor' && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_23rem]">
          <section aria-label="El formulario, tal como se verá">
            <Lienzo
              alDesplazar={desplazarCampo}
              alDuplicar={duplicarCampo}
              alElegir={elegir}
              alInsertar={(tipo, posicion) => anadir(tipo, posicion, false)}
              alMover={moverCampo}
              alQuitar={quitarCampo}
              bloques={bloques}
              campos={campos}
              enfocar={enfocar}
              errores={errores}
              estiloDeMarca={marca.estilo}
              moneda={marca.moneda}
              pedirCampo={setPidiendo}
              seleccion={indiceElegido >= 0 ? indiceElegido : null}
              sitio={sitio}
              soloLectura={soloLectura}
            />
          </section>
          {ancha && (
            <aside aria-label="Campos y ajustes">
              <div className="sticky top-[11.25rem] h-[calc(100vh-15.75rem)] min-h-96 overflow-hidden rounded-caja border border-linea bg-papel shadow-suave">
                {barraLateral}
              </div>
            </aside>
          )}
        </div>
      )}

      {pestana === 'ajustes' && (
        <AjustesDelFormulario
          activa={seccion}
          alBorrar={() => void edicion.borrar()}
          alCambiar={setSeccion}
          alDuplicar={() => void edicion.duplicar()}
          errores={errores}
          espacio={espacio}
          id={edicion.id}
          puedeBorrar={edicion.puedeBorrar}
          puedeDuplicar={edicion.puedeDuplicar}
          secciones={secciones}
          sitio={sitio}
          sucio={edicion.sucio}
        />
      )}

      {pestana === 'respuestas' && edicion.id !== null && (
        <RespuestasDeUnFormulario
          espacio={espacio}
          formulario={{ id: edicion.id, nombre: String(valores.nombre ?? '') }}
          sitio={sitio}
        />
      )}

      {/* En una pantalla estrecha, la barra de la derecha es un cajón que se abre desde abajo. */}
      {!ancha && pestana === 'editor' && !soloLectura && (
        <div className="fixed bottom-4 left-4 z-20 flex gap-2">
          <Boton
            className="shadow-alta"
            icono={<Plus />}
            onClick={() => {
              setLateral('anadir')
              setCajon(true)
            }}
            tipo="primario"
          >
            Añadir campo
          </Boton>
          <Boton
            className="shadow-alta"
            disabled={!fila}
            icono={<SlidersHorizontal />}
            onClick={() => {
              setLateral('ajustes')
              setCajon(true)
            }}
          >
            Ajustes
          </Boton>
        </div>
      )}
      {!ancha && cajon && pestana === 'editor' && (
        <Modal
          cerrar={() => setCajon(false)}
          titulo="Campos del formulario"
          ancho="max-w-lg"
        >
          <div className="-m-5 flex h-[70vh] flex-col">{barraLateral}</div>
        </Modal>
      )}

      {pidiendo !== null && (
        <Modal ancho="max-w-lg" cerrar={() => setPidiendo(null)} titulo="Añadir un campo">
          <Paleta
            alElegir={(tipo) => {
              anadir(tipo, pidiendo, true)
              setPidiendo(null)
            }}
            arrastrable={false}
            autoFoco
            bloques={bloques}
          />
        </Modal>
      )}

      {quitado && (
        <div
          className="fixed bottom-20 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-caja border border-linea bg-papel px-3 py-2 text-[13px] shadow-alta"
          role="status"
        >
          <span className="max-w-64 truncate">
            «{quitado}» quitado. No se guarda hasta que pulses «Guardar».
          </span>
          <Boton icono={<Undo2 />} onClick={deshacerUno} pequeno>
            Deshacer
          </Boton>
          <BotonDeIcono etiqueta="Cerrar" onClick={() => setQuitado(null)} pequeno>
            <X />
          </BotonDeIcono>
        </div>
      )}

      {previa && (
        <VistaPrevia
          cerrar={() => {
            setPrevia(false)
            if (deLaDireccion === 'vista-previa') irA('editor')
          }}
          documento={valores}
          espacio={espacio}
          estiloDeMarca={marca.estilo}
          id={edicion.id}
          moneda={marca.moneda}
          sitio={sitio}
        />
      )}
    </div>
  )
}

/** El nombre del formulario, editable en la cabecera: es lo primero que se lee y lo que lo distingue en el listado. */
const NombreDelFormulario = ({ soloLectura }: { soloLectura: boolean }) => {
  const { valor, cambiar, error } = useValor<string>('nombre')
  return (
    <div className="min-w-0 flex-1 basis-64">
      <input
        aria-describedby={error ? 'nombre-error' : undefined}
        aria-invalid={Boolean(error)}
        aria-label="Nombre del formulario"
        className={clases(
          '-ml-2 w-full min-w-0 truncate rounded-boton border bg-transparent px-2 py-0.5 font-display text-[26px] leading-tight font-semibold tracking-tight text-tinta transition-colors placeholder:text-tenue focus:bg-papel focus:outline-none',
          error ? 'border-error' : 'border-transparent hover:border-linea focus:border-violeta',
        )}
        id="nombre"
        onChange={(e) => cambiar(e.target.value)}
        placeholder="Ponle un nombre"
        readOnly={soloLectura}
        value={valor ?? ''}
      />
      {error && (
        <p className="text-xs text-error" id="nombre-error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
